// DOM primitives shared by MentionEditor and its suggestion hook: chip /
// draft nodes, hydration from server content, and caret placement.
import {
  MENTION_CHIP_CLASS,
  parseServerContent,
  type MentionMeta,
} from './mentionUtils';

// The in-progress `@query` is wrapped in this span so it renders in the
// mention color while typing. It carries no data — serialization walks into
// it and emits its plain text — and is unwrapped on dismiss or replaced by a
// chip on selection.
export const MENTION_DRAFT_CLASS = 'agentforge-mention-draft';

export const QUERY_REGEX = /(?:^|\s)@(\S*)$/;
export const DRAFT_REGEX = /^@(\S*)$/;

export const isSelfOrAncestorChip = (node: Node | null): boolean => {
  let cur: Node | null = node;
  while (cur) {
    if (
      cur.nodeType === Node.ELEMENT_NODE &&
      (cur as HTMLElement).classList?.contains(MENTION_CHIP_CLASS)
    ) {
      return true;
    }
    cur = cur.parentNode;
  }
  return false;
};

export const buildChipNode = (meta: MentionMeta): HTMLSpanElement => {
  const span = document.createElement('span');
  span.className = MENTION_CHIP_CLASS;
  span.setAttribute('contenteditable', 'false');
  span.dataset.mentionId = meta.id;
  span.dataset.mentionEntity = meta.entity;
  span.dataset.mentionName = meta.name;
  span.textContent = `@${meta.name}`;
  return span;
};

// Populate a contenteditable root from server-format content. Newlines in
// text segments become `<br>` so the editor lays them out.
export const hydrateRoot = (root: HTMLElement, initial: string): void => {
  root.innerHTML = '';
  const tokens = parseServerContent(initial);
  tokens.forEach((token) => {
    if (token.type === 'mention') {
      root.appendChild(buildChipNode(token.mention));
      return;
    }
    const lines = token.text.split('\n');
    lines.forEach((line, i) => {
      if (i > 0) root.appendChild(document.createElement('br'));
      if (line.length > 0) root.appendChild(document.createTextNode(line));
    });
  });
};

export const moveCaretAfter = (node: Node): void => {
  const sel = window.getSelection();
  if (!sel) return;
  const range = document.createRange();
  range.setStartAfter(node);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
};

export const focusEnd = (el: HTMLElement): void => {
  el.focus();
  const sel = window.getSelection();
  if (!sel) return;
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
};

// Caret to the end of a node's contents.
export const caretToEndOf = (node: Node): void => {
  const sel = window.getSelection();
  if (!sel) return;
  const r = document.createRange();
  r.selectNodeContents(node);
  r.collapse(false);
  sel.removeAllRanges();
  sel.addRange(r);
};
